import React, { useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Box } from "@mui/material";
import DashboardLayout from "../../layout/DashboardLayout/DashboardLayout";
import {
  Table,
  Input,
  Button,
  DatePicker,
  Space,
  Tag,
  ConfigProvider,
  Row,
  Col,
  Card,
  Typography,
  Avatar,
  Dropdown,
  message,
  Spin,
  Popover,
  Select,
  Image,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  FileImageOutlined,
  DownOutlined
} from "@ant-design/icons";
import services from "../../services";
import emptyStateImage from "../../assets/images/Frame 1171275820.png"; // Assuming this exists as used in TripVerification
import manageChallengesIcon from "../../assets/images/manage_challanges_icon.png";
import "./styles.css";
import CreateChallengeModal from "./CreateChallengeModal";
import ConfirmationModal from "./ConfirmationModal";


const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
dayjs.extend(customParseFormat);

const ManageChallenges = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  // Confirmation Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmType, setConfirmType] = useState('create'); // create, update, delete, status, success
  const [confirmData, setConfirmData] = useState(null);
  const [successInfo, setSuccessInfo] = useState({ title: '', description: '' });
  const [confirmLoading, setConfirmLoading] = useState(false);


  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [pendingData, setPendingData] = useState(null); // Store payload for API call

  // Filter States
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    challengeName: undefined,
    tripId: "",
    type: undefined,
    mode: undefined,
    effectiveDateRange: null,
    status: undefined
  });

  // Temporary state for the popover inputs
  const [tempFilters, setTempFilters] = useState(filters);

  // Handle opening/closing popover
  const handleOpenChange = (newOpen) => {
    setFilterOpen(newOpen);
    if (newOpen) {
      setTempFilters(filters); // Reset temp to current applied filters on open
    }
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    const cleared = {
      challengeName: undefined,
      tripId: "",
      type: undefined,
      mode: undefined,
      effectiveDateRange: null,
      status: undefined
    };
    setTempFilters(cleared);
    setFilters(cleared);
  };

  const fetchChallenges = async (page = 1, size = 10) => {
    setIsLoading(true);
    try {
      // API call
      const response = await services.getAllChallenges(page, size);
      console.log('Fetch challenges response:', response);
      console.log('Full response data:', response.data);

      // Check for error attribute (true means error)
      if (response.data?.error === true) {
        message.error(response.data?.message || 'Failed to fetch challenges');
        setData([]);
        return;
      }

      // Handle different response structures
      // New API format: response.data.data.challenges
      // Old API format: response.data.content or response.data.data (array directly)
      let challenges = [];
      let total = 0;

      if (response.data?.data?.challenges) {
        // New API structure
        challenges = response.data.data.challenges;
        total = response.data.data.totalCount || response.data.data.totalElements || 0;
        console.log('Using new API structure - challenges:', challenges.length, 'total:', total);
      } else if (Array.isArray(response.data?.data)) {
        // Data is directly an array
        challenges = response.data.data;
        total = response.data.total || challenges.length;
        console.log('Data is array - challenges:', challenges.length, 'total:', total);
      } else if (Array.isArray(response.data?.content)) {
        // Old paginated structure
        challenges = response.data.content;
        total = response.data.totalElements || response.data.total || 0;
        console.log('Using old API structure - challenges:', challenges.length, 'total:', total);
      } else {
        console.warn('Unknown response structure:', response.data);
        challenges = [];
        total = 0;
      }

      const formattedData = challenges.map(item => ({
        key: item.id,
        id: item.id, // Explicit ID
        name: item.name,
        description: item.description,
        type: item.type, // Enum
        co2ToSave: `${item.co2Saved || 0} kg`,
        co2Saved: item.co2Saved, // Raw value
        mode: item.mode, // Enum
        rewardBadge: item.rewardBadgeImageUrl ? "Badge Present" : "No Badge",
        rewardBadgeImageUrl: item.rewardBadgeImageUrl,
        challengeImage: item.challengeImageUrl ? "Image Present" : "No Image",
        challengeImageUrl: item.challengeImageUrl,
        effectiveFrom: dayjs(item.effectiveDateFrom).format("DD/MM/YY h:mm a"),
        effectiveTo: dayjs(item.effectiveDateTo).format("DD/MM/YY h:mm a"),
        effectiveDateFrom: item.effectiveDateFrom, // Raw ISO
        effectiveDateTo: item.effectiveDateTo, // Raw ISO
        frequency: item.frequency,
        repetition: item.repetition,
        participation: 0,
        status: item.status,
        keyRequirements: item.keyRequirements // Raw
      }));

      console.log('Formatted data:', formattedData.length, 'items');
      setData(formattedData);
      setPagination(prev => ({ ...prev, current: page, pageSize: size, total: total }));
    } catch (error) {
      console.error("Failed to fetch challenges:", error);
      console.error("Error response:", error.response);

      // Check if error response has error attribute
      if (error.response?.data?.error === true) {
        message.error(error.response.data?.message || 'Failed to fetch challenges');
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to fetch challenges. Please try again.');
      }

      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchChallenges(pagination.current, pagination.pageSize);
  }, []);

  const handleTableChange = (newPagination) => {
    fetchChallenges(newPagination.current, newPagination.pageSize);
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedChallenge(null);
    setIsModalOpen(true);
  };

  const handleView = (record) => {
    setModalMode('view');
    // Map record to initialData structure expected by modal
    setSelectedChallenge({
      ...record,
      descriptionFull: record.description,
      frequencyNumeric: record.frequency
    });
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setModalMode('edit');
    setSelectedChallenge({
      ...record,
      descriptionFull: record.description,
      frequencyNumeric: record.frequency
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (payload) => {
    // Trigger confirmation instead of immediate close
    setIsModalOpen(false);
    setPendingData(payload); // Store the payload from the form
    setConfirmType(modalMode === 'create' ? 'create' : 'update');
    setShowConfirm(true);
  };

  const handleDelete = (record) => {
    setConfirmData(record);
    setConfirmType('delete');
    setShowConfirm(true);
  };

  const handleStatusChange = (status, record) => {
    setConfirmData({ ...record });
    setPendingData({ status: status.toUpperCase() }); // Partial update payload
    setConfirmType('status');
    setShowConfirm(true);
  };

  const handleConfirmAction = async () => {
    // Perform action based on confirmType
    let title = '';
    let description = '';

    setConfirmLoading(true);

    try {
      let response;

      if (confirmType === 'create') {
        response = await services.createChallenge(pendingData);
        console.log('Create challenge response:', response);
        console.log('Create challenge full data:', response.data);

        // Check for error attribute (truthy or success false means error)
        if (response.data?.error || response.data?.success === false) {
          message.error(response.data?.message || 'Failed to create challenge');
          setShowConfirm(false);
          return;
        }

        title = 'Challenge created';
        description = response.data?.message || 'The challenge has been created successfully.';
        message.success(response.data?.message || 'Challenge created successfully!');

      } else if (confirmType === 'update') {
        response = await services.updateChallenge(selectedChallenge.id, pendingData);
        console.log('Update challenge response:', response);
        console.log('Update challenge full data:', response.data);

        // Check for error attribute
        if (response.data?.error || response.data?.success === false) {
          message.error(response.data?.message || 'Failed to update challenge');
          setShowConfirm(false);
          return;
        }

        title = 'Challenge Updated';
        description = response.data?.message || 'The challenge has been updated successfully.';
        message.success(response.data?.message || 'Challenge updated successfully!');

      } else if (confirmType === 'delete') {
        response = await services.deleteChallenge(confirmData.id);
        console.log('Delete challenge response:', response);
        console.log('Delete challenge full data:', response.data);

        // Check for error attribute
        if (response.data?.error || response.data?.success === false) {
          message.error(response.data?.message || 'Failed to delete challenge');
          setShowConfirm(false);
          return;
        }

        title = 'Challenge Deleted';
        description = response.data?.message || 'The challenge has been deleted successfully.';
        message.success(response.data?.message || 'Challenge deleted successfully!');

      } else if (confirmType === 'status') {
        response = await services.updateChallenge(confirmData.id, pendingData);
        console.log('Update status response:', response);
        console.log('Update status full data:', response.data);

        // Check for error attribute
        if (response.data?.error || response.data?.success === false) {
          message.error(response.data?.message || 'Failed to update status');
          setShowConfirm(false);
          return;
        }

        title = 'Status Updated';
        description = response.data?.message || 'The challenge status has been updated.';
        message.success(response.data?.message || 'Status updated successfully!');
      }

      // Log response code and statusCode if available
      if (response?.data?.code) {
        console.log('Response code:', response.data.code);
      }
      if (response?.data?.statusCode) {
        console.log('Response statusCode:', response.data.statusCode);
      }

      setSuccessInfo({ title, description });
      setConfirmType('success');
      fetchChallenges(pagination.current, pagination.pageSize); // Refresh table

    } catch (error) {
      console.error("Operation failed:", error);
      console.error("Error response:", error.response);

      // Check if error response has error attribute
      if (error.response?.data?.error === true) {
        message.error(error.response.data?.message || 'Operation failed');
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error(error.message || 'An unexpected error occurred');
      }

      setShowConfirm(false);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCloseConfirm = () => {
    setShowConfirm(false);
    setConfirmType('create'); // Reset default
    setPendingData(null); // Clear pending data
    setConfirmData(null); // Clear confirm data
    setSelectedChallenge(null); // Clear selected challenge
    setSuccessInfo({ title: '', description: '' }); // Clear success info
  };

  // Dummy data matching the image removed

  /* Helper functions for Status Column Styling */
  const getStatusColor = (status) => {
    // Background is always white/transparent for the "outline" look in the new design
    return '#FFFFFF';
  };

  const getStatusTextColor = (status) => {
    switch (status?.toUpperCase()) { // Ensure case insensitivity
      case 'ACTIVE': return '#12B76A'; // Teal/Mint Green text (approx matching design)
      case 'INACTIVE': return '#F04438'; // Red text
      case 'SELECT': return '#667085'; // Gray text
      default: return '#344054';
    }
  };

  const getBorderColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return '#6CE9A6'; // Mint Green border
      case 'INACTIVE': return '#F04438'; // Red border
      case 'SELECT': return '#D0D5DD'; // Gray border
      default: return '#EAECF0';
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'MONTHLY': return { color: '#6941C6', bg: '#F9F5FF' };
      case 'DAILY': return { color: '#175CD3', bg: '#EFF8FF' }; // Blue for Daily
      case 'WEEKLY': return { color: '#C11574', bg: '#FDF2FA' };
      default: return { color: '#344054', bg: '#F2F4F7' };
    }
  };


  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (text) => <Text type="secondary" style={{ fontSize: '12px' }}>{text}</Text>
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120, // Slightly wider for pill
      render: (text) => {
        const style = getTypeColor(text);
        return (
          <span style={{
            color: style.color,
            backgroundColor: style.bg,
            padding: '2px 10px',
            borderRadius: '16px',
            fontWeight: 500,
            display: 'inline-block'
          }}>
            {text}
          </span>
        );
      }
    },
    {
      title: "Co2 to Save",
      dataIndex: "co2ToSave",
      key: "co2ToSave",
      width: 120,
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      width: 150,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Reward Badge",
      dataIndex: "rewardBadge",
      key: "rewardBadge",
      width: 250,
      render: (text, record) => {
        const badgeUrl = record.rewardBadgeImageUrl;
        const hasBadge = badgeUrl && (badgeUrl.startsWith('https://') || badgeUrl.startsWith('http://'));

        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            border: '1px solid #EAECF0',
            borderRadius: '8px',
            width: 'fit-content',
            minWidth: '200px'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            {hasBadge ? (
              // Show actual badge image from API
              <Image
                src={badgeUrl}
                alt="Badge"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  // Fallback to default icon if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            {/* Fallback hexagon icon - show only if no badge URL or image fails */}
            <div style={{
              width: '32px',
              height: '32px',
              position: 'relative',
              display: hasBadge ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute' }}>
                <path d="M16 2.66666L28.6667 9.33332V22.6667L16 29.3333L3.33334 22.6667V9.33332L16 2.66666Z" fill="#936F1F" stroke="#936F1F" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              <span style={{ position: 'relative', color: 'white', fontSize: '16px', zIndex: 1, display: 'flex' }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 3.5C7.9665 3.5 8.75 2.7165 8.75 1.75C8.75 0.783502 7.9665 0 7 0C6.0335 0 5.25 0.783502 5.25 1.75C5.25 2.7165 6.0335 3.5 7 3.5ZM9.1 5.05167L8.03833 9.42667L9.625 11.0133C9.84083 11.2292 9.84083 11.5792 9.625 11.795C9.40917 12.0108 9.05917 12.0108 8.84333 11.795L7.14583 10.0975C7.02917 9.98083 6.965 9.82333 6.965 9.66L7.26833 8.16667L6.0025 7.55417L4.725 9.47333C4.85333 9.63667 4.92917 9.84083 4.92917 10.0625V13.125C4.92917 13.4458 4.66667 13.7083 4.34583 13.7083C4.025 13.7083 3.7625 13.4458 3.7625 13.125V10.3542L5.27917 8.07917L4.66667 7.7875L4.025 8.42917C3.59333 8.86083 2.89333 8.86083 2.46167 8.42917C2.03 7.9975 2.03 7.2975 2.46167 6.86583L4.99333 4.33417C5.32 4.0075 5.83917 3.93167 6.24167 4.1475L7.7 4.935L8.715 3.92C8.84333 3.79167 9.01833 3.72167 9.19917 3.72167H11.375C11.6958 3.72167 11.9583 3.98417 11.9583 4.305C11.9583 4.62583 11.6958 4.88833 11.375 4.88833H9.49667L9.1 5.05167Z" fill="white" />
                </svg>
              </span>
            </div>
            <Text style={{ fontWeight: 500, color: '#344054' }}>{text}</Text>
          </div>
        );
      }
    },
    {
      title: "Challenge Image",
      dataIndex: "challengeImage",
      key: "challengeImage",
      width: 200,
      render: (text, record) => {
        const imageUrl = record.challengeImageUrl;
        const hasImage = imageUrl && (imageUrl.startsWith('https://') || imageUrl.startsWith('http://'));

        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            border: '1px solid #EAECF0',
            borderRadius: '8px',
            width: 'fit-content',
            cursor: 'pointer'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            {hasImage ? (
              <Image
                src={imageUrl}
                alt="Challenge"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <FileImageOutlined
              style={{
                fontSize: '32px',
                color: '#00A3BF',
                display: hasImage ? 'none' : 'block'
              }}
            />
            <Text style={{ fontWeight: 500, color: '#344054' }}>{text}</Text>
          </div>
        );
      }
    },
    {
      title: "Effective From",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      width: 160,
      render: (text) => <Text type="secondary" style={{ fontSize: '13px' }}>{text}</Text>
    },
    {
      title: "Effective To",
      dataIndex: "effectiveTo",
      key: "effectiveTo",
      width: 160,
      render: (text) => <Text type="secondary" style={{ fontSize: '13px' }}>{text}</Text>
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      width: 100,
    },
    {
      title: "Repetition",
      dataIndex: "repetition",
      key: "repetition",
      width: 100,
    },
    {
      title: "Participation",
      dataIndex: "participation",
      key: "participation",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180, // Increased column width to accommodate larger dropdown
      render: (text, record) => {
        const items = [
          { label: 'Active', key: 'Active' },
          { label: 'Inactive', key: 'Inactive' },
        ];

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{
                items,
                onClick: ({ key }) => {
                  handleStatusChange(key, record);
                }
              }}
              trigger={['click']}
            >
              <div
                style={{
                  border: `1.5px solid ${getBorderColor(text)}`,
                  backgroundColor: getStatusColor(text),
                  color: getStatusTextColor(text),
                  borderRadius: '10px',
                  padding: '8px 16px',
                  textAlign: 'left',
                  fontSize: '15px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  width: '160px',
                  height: '48px'
                }}>
                {text?.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
                <DownOutlined style={{ fontSize: '14px', color: getStatusTextColor(text), fontWeight: 'bold' }} />
              </div>
            </Dropdown>
          </div>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined
            style={{ color: '#667085', fontSize: '16px', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); handleView(record); }}
          />
          <EditOutlined
            style={{ color: '#667085', fontSize: '16px', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); handleEdit(record); }}
          />
          <DeleteOutlined
            style={{ color: '#667085', fontSize: '16px', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); handleDelete(record); }}
          />
        </Space>
      ),
    },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Get unique lists for dropdowns from current data
  const challengeNames = [...new Set(data.map(item => item.name))];
  const challengeTypes = [...new Set(data.map(item => item.type))];
  const challengeModes = [...new Set(data.map(item => item.mode))];

  const filterContent = (
    <div style={{ width: 600, padding: 8 }}>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Text strong>Challenge Name</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.challengeName}
            onChange={(val) => setTempFilters({ ...tempFilters, challengeName: val })}
          >
            <Select.Option value="All">All</Select.Option>
            {challengeNames.map(name => <Select.Option key={name} value={name}>{name}</Select.Option>)}
          </Select>
        </Col>
        <Col span={12}>
          <Text strong>Trip ID</Text>
          <Input
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Search by ID"
            value={tempFilters.tripId}
            onChange={(e) => setTempFilters({ ...tempFilters, tripId: e.target.value })}
          />
        </Col>
        <Col span={12}>
          <Text strong>Type</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.type}
            onChange={(val) => setTempFilters({ ...tempFilters, type: val })}
          >
            <Select.Option value="All">All</Select.Option>
            {challengeTypes.map(type => <Select.Option key={type} value={type}>{type}</Select.Option>)}
          </Select>
        </Col>
        <Col span={12}>
          <Text strong>Mode</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.mode}
            onChange={(val) => setTempFilters({ ...tempFilters, mode: val })}
          >
            <Select.Option value="All">All</Select.Option>
            {challengeModes.map(mode => <Select.Option key={mode} value={mode}>{mode}</Select.Option>)}
          </Select>
        </Col>
        <Col span={12}>
          <Text strong>Effective from & to</Text>
          <RangePicker
            style={{ width: '100%', marginTop: 8 }}
            value={tempFilters.effectiveDateRange}
            onChange={(dates) => setTempFilters({ ...tempFilters, effectiveDateRange: dates })}
          />
        </Col>
        <Col span={12}>
          <Text strong>Status</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.status}
            onChange={(val) => setTempFilters({ ...tempFilters, status: val })}
          >
            <Select.Option value="All">All</Select.Option>
            <Select.Option value="ACTIVE">Active</Select.Option>
            <Select.Option value="INACTIVE">Inactive</Select.Option>
          </Select>
        </Col>
      </Row>
      <Row justify="end" style={{ marginTop: 24, gap: 12 }}>
        <Button onClick={handleClearFilters}>Clear</Button>
        <Button type="primary" onClick={handleApplyFilters} style={{ background: '#5FB92A' }}>Apply</Button>
      </Row>
    </div>
  );

  const handleExport = () => {
    // Define headers
    const headers = [
      "Name",
      "Description",
      "Type",
      "Co2 to Save",
      "Mode",
      "Reward Badge",
      "Challenge Image",
      "Effective From",
      "Effective To",
      "Frequency",
      "Repetition",
      "Participation",
      "Status"
    ];

    // Map data to CSV rows
    const rows = data.map(item => [
      `"${item.name}"`, // Quote strings that might contain commas
      `"${item.description}"`,
      item.type,
      item.co2ToSave,
      item.mode,
      item.rewardBadge,
      item.challengeImage,
      item.effectiveFrom,
      item.effectiveTo,
      item.frequency,
      item.repetition,
      item.participation,
      item.status
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "challenges_export.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#5FB92A",
          fontFamily: "'Manrope', sans-serif",
          borderRadius: 8,
        },
      }}
    >
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Card bordered={false} style={{ boxShadow: 'none' }}>
            {/* Header */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
              <Col>
                <Title level={3} style={{ margin: 0, fontWeight: 600 }}>Manage Challenges</Title>
                <Text type="secondary">Create and manage challenges</Text>
              </Col>
              <Col>
                <Space>
                  <Button icon={<DownloadOutlined />} size="large" onClick={handleExport}>Export</Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    style={{ backgroundColor: '#5FB92A' }}
                    onClick={handleCreate}
                  >
                    Create Challenge
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }} align="middle">
              <Col flex="1 1 300px">
                <Input
                  size="large"
                  placeholder="Search for Challenges"
                  prefix={<SearchOutlined style={{ color: '#667085' }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>
              <Col flex="0 1 auto">
                <Space>
                  <RangePicker
                    size="large"
                    onChange={(dates) => setDateRange(dates)}
                  />
                  <Popover
                    content={filterContent}
                    title="Filter Challenges"
                    trigger="click"
                    open={filterOpen}
                    onOpenChange={handleOpenChange}
                    placement="bottomRight"
                  >
                    <Button icon={<FilterOutlined />} size="large">Filters</Button>
                  </Popover>
                </Space>
              </Col>
            </Row>

            {/* Table */}
            <div className="manage-challenges-pagination-wrapper">
              {isLoading && (
                <div className="table-progress-bar">
                  <div className="table-progress-bar-inner" />
                </div>
              )}
                <Table
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={data.filter((item) => {
                    let matchesSearch = true;
                    let matchesDate = true;
                    let matchesFilters = true;

                    // Search filter
                    if (searchText) {
                      const searchLower = searchText.toLowerCase();
                      matchesSearch =
                        item.name?.toLowerCase().includes(searchLower) ||
                        item.description?.toLowerCase().includes(searchLower);
                    }

                    // Header date range filter
                    if (dateRange && dateRange[0] && dateRange[1]) {
                      // Parse "DD/MM/YY h:mm a" format, e.g., "12/11/25 8:45 pm"
                      const itemDate = dayjs(item.effectiveFrom, "DD/MM/YY h:mm a");

                      if (itemDate.isValid()) {
                        const start = dateRange[0].startOf('day');
                        const end = dateRange[1].endOf('day');
                        matchesDate = itemDate.isAfter(start) && itemDate.isBefore(end) || itemDate.isSame(start) || itemDate.isSame(end);
                      } else {
                        matchesDate = false;
                      }
                    }

                    // Advanced filters
                    if (filters.challengeName && filters.challengeName !== 'All') {
                      matchesFilters = matchesFilters && item.name === filters.challengeName;
                    }
                    if (filters.tripId) {
                      matchesFilters = matchesFilters && item.id?.toString().toLowerCase().includes(filters.tripId.toLowerCase());
                    }
                    if (filters.type && filters.type !== 'All') {
                      matchesFilters = matchesFilters && item.type === filters.type;
                    }
                    if (filters.mode && filters.mode !== 'All') {
                      matchesFilters = matchesFilters && item.mode === filters.mode;
                    }
                    if (filters.status && filters.status !== 'All') {
                      matchesFilters = matchesFilters && item.status?.toUpperCase() === filters.status.toUpperCase();
                    }
                    if (filters.effectiveDateRange && filters.effectiveDateRange[0] && filters.effectiveDateRange[1]) {
                      const itemDate = dayjs(item.effectiveDateFrom);
                      if (itemDate.isValid()) {
                        const start = filters.effectiveDateRange[0].startOf('day');
                        const end = filters.effectiveDateRange[1].endOf('day');
                        matchesFilters = matchesFilters && (itemDate.isAfter(start) && itemDate.isBefore(end) || itemDate.isSame(start) || itemDate.isSame(end));
                      } else {
                        matchesFilters = false;
                      }
                    }

                    return matchesSearch && matchesDate && matchesFilters;
                  })}
                  onRow={(record) => ({
                    onClick: () => {
                      handleView(record);
                    },
                    style: { cursor: 'pointer' }
                  })}
                  pagination={{
                    position: ["bottomCenter"],
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: false,
                    itemRender: (current, type, originalElement) => {
                      const totalPages = Math.ceil(pagination.total / pagination.pageSize);
                      if (type === 'prev') {
                        if (totalPages <= 1) return null;
                        const isFirstPage = pagination.current <= 1;
                        return (
                          <Button
                            className="custom-pagination-link"
                            disabled={isFirstPage}
                          >
                            ← Previous
                          </Button>
                        );
                      }
                      if (type === 'next') {
                        if (totalPages <= 1) return null;
                        const isLastPage = pagination.current >= totalPages;
                        return (
                          <Button
                            className="custom-pagination-link"
                            disabled={isLastPage}
                          >
                            Next →
                          </Button>
                        );
                      }
                      return originalElement;
                    },
                  }}
                  onChange={handleTableChange}
                  scroll={{ x: 'max-content' }}
                  style={{ background: '#fff' }}
                  locale={{
                    emptyText: (
                      <div style={{
                        padding: '60px 20px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          width: '120px',
                          height: '120px',
                          margin: '0 auto 24px',
                          backgroundColor: '#ECFDF3', // Light green bg
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img
                            src={manageChallengesIcon}
                            alt="No challenges"
                            style={{ width: '60px', height: '60px' }}
                          />
                        </div>
                        <Title level={4} style={{ marginBottom: '8px', color: '#1F2937' }}>
                          No challenges available at the moment.
                        </Title>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          New challenges will appear here once<br />
                          you create them.
                        </Text>
                      </div>
                    )
                  }}
                />
            </div>
          </Card>
          <CreateChallengeModal
            open={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedChallenge(null);
              setModalMode('create');
            }}
            mode={modalMode}
            initialData={selectedChallenge}
            onEdit={() => setModalMode('edit')}
            onSubmit={handleSubmit}
          />

          <ConfirmationModal
            open={showConfirm}
            onCancel={handleCloseConfirm}
            onConfirm={confirmType === 'success' ? handleCloseConfirm : handleConfirmAction}
            type={confirmType}
            isSuccess={confirmType === 'success'}
            title={
              confirmType === 'success' ? successInfo.title :
                confirmType === 'create' ? 'Create New Challenge' :
                  confirmType === 'update' ? 'Confirm Challenge Update' :
                    confirmType === 'delete' ? 'Delete Challenge' :
                      confirmType === 'status' ? 'Update Challenge Status' : ''
            }
            description={
              confirmType === 'success' ? successInfo.description :
                confirmType === 'create' ? 'Please confirm creating this challenge. All configured details will be saved.' :
                  confirmType === 'update' ? 'Do you want to update this challenge? The new changes will override the existing data.' :
                    confirmType === 'delete' ? 'This action is permanent and cannot be undone. Are you sure you want to delete this challenge from the system?' :
                      confirmType === 'status' ? 'Are you sure you want to change the status of this challenge? This update will reflect immediately for all users.' : ''
            }
            confirmText={
              confirmType === 'create' ? 'Create' :
                confirmType === 'update' ? 'Update' :
                  confirmType === 'delete' ? 'Delete' :
                    confirmType === 'status' ? 'Save Changes' : 'Confirm'
            }
            loading={confirmLoading}
          />
        </Box>
      </DashboardLayout>
    </ConfigProvider>
  );
};

export default ManageChallenges;
